import { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X, Check, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getCustomers,
  createCustomer,
  updateCustomer,
  deleteCustomer,
} from "@/services/api";

const COLORS = [
  "#F59E0B", // Amber
  "#10B981", // Emerald
  "#3B82F6", // Blue
  "#8B5CF6", // Violet
  "#EC4899", // Pink
  "#EF4444", // Red
  "#06B6D4", // Cyan
  "#F97316", // Orange
];

export default function Clients() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    color: COLORS[0],
  });

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error("Error loading customers:", error);
      toast.error("Fehler beim Laden der Kunden");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (customer = null) => {
    if (customer) {
      setSelectedCustomer(customer);
      setFormData({
        name: customer.name,
        email: customer.email || "",
        phone: customer.phone || "",
        notes: customer.notes || "",
        color: customer.color || COLORS[0],
      });
    } else {
      setSelectedCustomer(null);
      setFormData({
        name: "",
        email: "",
        phone: "",
        notes: "",
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Bitte gib einen Namen ein");
      return;
    }

    setSaving(true);
    try {
      if (selectedCustomer) {
        await updateCustomer(selectedCustomer.id, formData);
        toast.success("Kunde aktualisiert");
      } else {
        await createCustomer(formData);
        toast.success("Kunde erstellt");
      }
      setDialogOpen(false);
      loadCustomers();
    } catch (error) {
      console.error("Error saving customer:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedCustomer) return;

    try {
      await deleteCustomer(selectedCustomer.id);
      toast.success("Kunde gelöscht");
      setDeleteDialogOpen(false);
      setSelectedCustomer(null);
      loadCustomers();
    } catch (error) {
      console.error("Error deleting customer:", error);
      toast.error("Fehler beim Löschen");
    }
  };

  const openDeleteDialog = (customer) => {
    setSelectedCustomer(customer);
    setDeleteDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="clients-loading">
        <div className="animate-pulse text-zinc-500">Laden...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Kunden
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {customers.length} {customers.length === 1 ? "Kunde" : "Kunden"}
          </p>
        </div>
        <Button
          onClick={() => openDialog()}
          className="bg-amber-500 hover:bg-amber-600 text-black font-semibold rounded-xl h-10 px-4 glow-amber"
          data-testid="add-customer-button"
        >
          <Plus size={18} className="mr-1" />
          Neu
        </Button>
      </div>

      {/* Customer List */}
      {customers.length === 0 ? (
        <div className="empty-state" data-testid="empty-customers">
          <User size={48} className="text-zinc-700 mb-4" />
          <p className="text-zinc-500 mb-2">Noch keine Kunden</p>
          <Button
            onClick={() => openDialog()}
            variant="outline"
            className="border-zinc-700 hover:bg-zinc-800"
          >
            Ersten Kunden anlegen
          </Button>
        </div>
      ) : (
        <div className="space-y-3" data-testid="customer-list">
          {customers.map((customer) => (
            <Card
              key={customer.id}
              className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors"
              data-testid={`customer-${customer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center text-lg font-bold"
                    style={{ backgroundColor: `${customer.color}20`, color: customer.color }}
                  >
                    {customer.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-zinc-100 truncate">{customer.name}</p>
                    {customer.email && (
                      <p className="text-sm text-zinc-500 truncate">{customer.email}</p>
                    )}
                    {customer.phone && (
                      <p className="text-sm text-zinc-500">{customer.phone}</p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDialog(customer)}
                      className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800"
                      data-testid={`edit-customer-${customer.id}`}
                    >
                      <Pencil size={18} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openDeleteDialog(customer)}
                      className="text-zinc-400 hover:text-red-500 hover:bg-red-500/10"
                      data-testid={`delete-customer-${customer.id}`}
                    >
                      <Trash2 size={18} />
                    </Button>
                  </div>
                </div>
                {customer.notes && (
                  <p className="mt-3 text-sm text-zinc-400 border-t border-zinc-800 pt-3">
                    {customer.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedCustomer ? "Kunde bearbeiten" : "Neuer Kunde"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Kundenname"
                className="h-12 bg-zinc-950 border-zinc-800 focus:border-amber-500"
                data-testid="customer-name-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">E-Mail</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, email: e.target.value }))
                }
                placeholder="email@beispiel.de"
                className="h-12 bg-zinc-950 border-zinc-800 focus:border-amber-500"
                data-testid="customer-email-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Telefon</label>
              <Input
                value={formData.phone}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, phone: e.target.value }))
                }
                placeholder="+49 123 456789"
                className="h-12 bg-zinc-950 border-zinc-800 focus:border-amber-500"
                data-testid="customer-phone-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Notizen</label>
              <Textarea
                value={formData.notes}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, notes: e.target.value }))
                }
                placeholder="Zusätzliche Informationen..."
                className="min-h-20 bg-zinc-950 border-zinc-800 focus:border-amber-500 resize-none"
                data-testid="customer-notes-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Farbe</label>
              <div className="flex gap-2 flex-wrap" data-testid="color-picker">
                {COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setFormData((prev) => ({ ...prev, color }))}
                    className={`w-10 h-10 rounded-lg transition-all ${
                      formData.color === color
                        ? "ring-2 ring-white ring-offset-2 ring-offset-zinc-900 scale-110"
                        : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    data-testid={`color-${color}`}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1 h-12 border-zinc-700 hover:bg-zinc-800"
              >
                Abbrechen
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="flex-1 h-12 bg-amber-500 hover:bg-amber-600 text-black font-semibold"
                data-testid="save-customer-button"
              >
                {saving ? "Speichern..." : selectedCustomer ? "Aktualisieren" : "Erstellen"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle>Kunde löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {selectedCustomer?.name} wird gelöscht. Alle zugehörigen Zeiteinträge werden ebenfalls entfernt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 hover:bg-zinc-800">
              Abbrechen
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-customer"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
